/** Route key: `tab` or `tab/sub`. */
export type RouteKey = string;

export type RouteDef = {
  title?: string;
  parent?: RouteKey;
  saveTo?: RouteKey;
  showSave?: boolean;
  showBack?: boolean;
};

export type RouteTarget = {
  tab: string;
  sub: string | null;
  params: Record<string, string>;
};

export function routeKey(tab: string, sub?: string | null): RouteKey {
  return sub ? `${tab}/${sub}` : tab;
}

export function parseRouteKey(key: RouteKey): { tab: string; sub: string | null } {
  const slash = key.indexOf("/");
  if (slash === -1) return { tab: key, sub: null };
  return { tab: key.slice(0, slash), sub: key.slice(slash + 1) || null };
}

export function targetFromKey(key: RouteKey, params: Record<string, string> = {}): RouteTarget {
  const { tab, sub } = parseRouteKey(key);
  return { tab, sub, params: { ...params } };
}

export type RouteResolverContext = {
  businessCount?: number;
  isBusinessAdd?: boolean;
};

export const ROUTES: Record<RouteKey, RouteDef> = {
  settings: { title: "Settings" },
  "settings/general": { title: "General", parent: "settings", showBack: true },
  "settings/logo": {
    title: "Logo",
    parent: "settings/general",
    saveTo: "settings/general",
    showSave: true,
    showBack: true,
  },
  "settings/payment-terms": {
    title: "Payment terms",
    parent: "settings/general",
    saveTo: "settings/general",
    showSave: true,
    showBack: true,
  },
  "settings/email-sending": {
    title: "Email sending",
    parent: "settings/general",
    saveTo: "settings/general",
    showSave: true,
    showBack: true,
  },
  "settings/business": { title: "Business profiles", parent: "settings", showBack: true },
  "settings/business-add": {
    title: "Add business",
    parent: "settings/business",
    saveTo: "settings",
    showSave: true,
    showBack: true,
  },
  "settings/business-edit": {
    title: "Business details",
    parent: "settings/business",
    saveTo: "settings",
    showSave: true,
    showBack: true,
  },
  "settings/account": { title: "Account", parent: "settings", showBack: true },
  "settings/updates": { title: "App update", parent: "settings", showBack: true },
  "settings/help": { title: "Help & support", parent: "settings", showBack: true },
  customers: { title: "Customers" },
  "customers/add": {
    title: "Add customer",
    parent: "customers",
    saveTo: "customers",
    showSave: true,
    showBack: true,
  },
  "customers/edit": {
    title: "Edit customer",
    parent: "customers/view",
    saveTo: "customers/view",
    showSave: true,
    showBack: true,
  },
  "customers/view": { title: "Customer", parent: "customers", showBack: true },
  invoices: { title: "Invoices" },
  "invoices/create": {
    title: "Create invoice",
    parent: "invoices",
    showSave: true,
    showBack: true,
  },
  "invoices/success": { title: "Invoice saved", parent: "invoices" },
  quotes: { title: "Quotes" },
  "quotes/create": {
    title: "Create quote",
    parent: "quotes",
    showSave: true,
    showBack: true,
  },
  "quotes/success": { title: "Quote saved", parent: "quotes" },
};

/** Resolve static or dynamic parent for back/save navigation. */
export function resolveParent(
  current: RouteTarget,
  ctx: RouteResolverContext = {}
): RouteTarget | null {
  const key = routeKey(current.tab, current.sub);
  const def = ROUTES[key];
  if (!def?.parent) return null;

  if (key === "settings/business-add" || key === "settings/business-edit") {
    const multi = (ctx.businessCount ?? 0) > 1;
    if (multi && !ctx.isBusinessAdd && key === "settings/business-edit") {
      return targetFromKey("settings/business");
    }
    if (key === "settings/business-add" && multi) {
      return targetFromKey("settings/business");
    }
    return targetFromKey("settings");
  }

  if (key === "customers/edit" && current.params.name) {
    return targetFromKey("customers/view", { name: current.params.name });
  }

  return targetFromKey(def.parent);
}

export function resolveSaveTo(
  current: RouteTarget,
  ctx: RouteResolverContext = {}
): RouteTarget | null {
  const key = routeKey(current.tab, current.sub);
  const def = ROUTES[key];
  if (def?.saveTo) {
    if (def.saveTo === "customers/view" && current.params.name) {
      return targetFromKey("customers/view", { name: current.params.name });
    }
    return targetFromKey(def.saveTo);
  }
  return resolveParent(current, ctx);
}

export function currentRouteTarget(
  tab: string,
  sub: string | null,
  params: Record<string, string>
): RouteTarget {
  return { tab, sub, params: { ...params } };
}
